import { FC } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
} from '@mui/material';
import { styled } from '@mui/system';
import { DialogModal } from '../../components';
import { notifyMsg } from '../../configs';
import {
  useDeleteUnusedAssets,
  useGetUnusedAssets,
} from '../../api-http/assets';
import { useSnack } from '../../util/useSnack';
import { getStaticFileUrl } from '../../util/getStaticFileUrl';
import { AssetWithPlaceholder } from './AssetWithPlaceholder';

interface DeleteUnusedAssetsDialogProps {
  open: boolean;
  onClose: () => void;
  onDeleted: () => void;
}

const AssetRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const DeleteUnusedAssetsDialog: FC<DeleteUnusedAssetsDialogProps> = ({
  open,
  onClose,
  onDeleted,
}) => {
  const { showErrSnack, showSuccessSnack } = useSnack();

  const {
    data: unusedAssets,
    isLoading,
    error,
  } = useGetUnusedAssets({ enabled: open });

  const { mutate: deleteUnusedAssets, isLoading: isDeleting } =
    useDeleteUnusedAssets({
      onSuccess: (result) => {
        showSuccessSnack(
          `${result.deletedCount} ${notifyMsg.UNUSED_ASSETS_DELETE_SUCCESS}`,
        );
        onDeleted();
        onClose();
      },
      onError: () => showErrSnack(notifyMsg.DELETE_ATTEMPT_FAILED),
    });

  const hasUnusedAssets = Boolean(unusedAssets?.length);

  return (
    <DialogModal
      title="Delete unused assets"
      open={open}
      onClose={onClose}
      width={600}
    >
      {isLoading && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && error && (
        <Box p={2}>Failed to load unused assets. Please try again.</Box>
      )}

      {!isLoading && !error && !hasUnusedAssets && (
        <Box p={2}>{notifyMsg.NO_UNUSED_ASSETS_FOUND}</Box>
      )}

      {!isLoading && !error && hasUnusedAssets && (
        <>
          <Box mb={1}>
            The following {unusedAssets?.length} asset
            {unusedAssets?.length === 1 ? '' : 's'} are not used in any slide in
            this organisation and will be permanently deleted:
          </Box>
          <List sx={{ maxHeight: 400, overflowY: 'auto' }}>
            {unusedAssets?.map((asset) => (
              <ListItem key={asset._id} divider>
                <AssetRow>
                  {asset.fileType.includes('image') && (
                    <AssetWithPlaceholder
                      src={getStaticFileUrl(asset.thumbnailUrl || '')}
                      fileName={asset.fileName}
                      width="80px"
                      type="image"
                    />
                  )}
                  {asset.fileType.includes('video') && (
                    <AssetWithPlaceholder
                      src={getStaticFileUrl(asset.path)}
                      fileName={asset.fileName}
                      width="80px"
                      type="video"
                    />
                  )}
                  <ListItemText
                    primary={asset.fileName}
                    secondary={asset.fileType}
                  />
                </AssetRow>
              </ListItem>
            ))}
          </List>
        </>
      )}

      <Stack direction="row" spacing={2} justifyContent="flex-end" mt={3}>
        <Button onClick={onClose} disabled={isDeleting}>
          Cancel
        </Button>
        {hasUnusedAssets && (
          <Button
            variant="contained"
            color="error"
            disabled={isLoading || isDeleting}
            onClick={() => deleteUnusedAssets()}
          >
            {isDeleting
              ? 'Deleting...'
              : `Delete ${unusedAssets?.length} Asset${
                  unusedAssets?.length === 1 ? '' : 's'
                }`}
          </Button>
        )}
      </Stack>
    </DialogModal>
  );
};
